import { Helmet } from "react-helmet-async";
import SiteNavBar from "@/components/SiteNavBar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Monitor, MessageSquare, HelpCircle } from "lucide-react";
import { LANDING_COLORS } from "@/lib/landing-styles";
import { RevealOnScroll, FloatingOrbs } from "@/components/landing";

const FAQ = () => {
  return (
    <div className="min-h-screen font-['Inter']">
      <Helmet>
        <title>Google My Business Optimization FAQ | GMB Management Questions & Answers</title>
        <meta name="description" content="Get answers to frequently asked questions about Google My Business optimization, GMB management, local SEO services, pricing, setup time, and more. Expert GMB FAQ resource." />
        <meta name="keywords" content="Google My Business optimization FAQ, GMB management questions, Google My Business setup FAQ, local SEO services FAQ, Google Business Profile help, GMB optimization answers, Google My Business verification FAQ" />
        <link rel="canonical" href="https://fly-social.com/faq" />
        <meta property="og:title" content="Google My Business Optimization FAQ | GMB Management Questions & Answers" />
        <meta property="og:description" content="Get answers to frequently asked questions about Google My Business optimization, GMB management, local SEO services, pricing, setup time, and more." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://fly-social.com/faq" />
        <meta property="og:image" content="https://fly-social.com/lovable-uploads/ad42997b-b41b-49fe-8e06-dc69572da5bd.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Google My Business Optimization FAQ | GMB Management Questions & Answers" />
        <meta name="twitter:description" content="Get answers to frequently asked questions about Google My Business optimization, GMB management, local SEO services, pricing, setup time, and more." />
        <meta name="twitter:image" content="https://fly-social.com/lovable-uploads/ad42997b-b41b-49fe-8e06-dc69572da5bd.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "name": "Google My Business Optimization FAQ",
            "description": "Frequently asked questions about Google My Business optimization and management services",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What's the difference between Fly Web Dashboard and Fly WhatsApp Agent?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Fly Web Dashboard is our full-featured DIY platform where you manage everything yourself through a powerful web interface. Fly WhatsApp Agent is a hands-free solution where our AI assistant handles everything for you via WhatsApp. Choose Web Dashboard if you want full control and deep analytics. Choose WhatsApp Agent if you prefer a zero-effort, conversational experience."
                }
              },
              {
                "@type": "Question",
                "name": "How soon can I see results?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Most specialists see measurable improvements in 45-60 days as your optimized profile gains traction and attracts more client attention."
                }
              },
              {
                "@type": "Question",
                "name": "Do I still own my Google profile?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, you always own your profile. We simply optimize and manage it on your behalf while you retain complete ownership and control."
                }
              },
              {
                "@type": "Question",
                "name": "How does the WhatsApp Agent work?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Simply message Fly on WhatsApp like you would a colleague. Ask for your performance report, get notified about new reviews, approve AI-generated responses, and manage your profile — all through natural conversation. No apps to install, no dashboards to learn."
                }
              },
              {
                "@type": "Question",
                "name": "What features does the Web Dashboard include?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The Web Dashboard includes comprehensive analytics and reporting, multi-location management, AI-powered post scheduling, review management with AI response suggestions, competitor tracking, Google Business Profile optimization tools, and detailed performance insights."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <SiteNavBar />

      {/* Hero */}
      <section
        className="pt-24 md:pt-32 pb-16 relative overflow-hidden dark:bg-slate-950"
        style={{ background: 'linear-gradient(135deg, #faf5ff 0%, #f0fdf4 40%, #ecfeff 100%)' }}
      >
        <FloatingOrbs variant="hero" />
        <div className="container mx-auto px-4 sm:px-6 relative z-10 text-center">
          <RevealOnScroll>
            <span
              className="inline-flex items-center gap-1.5 px-4 py-1.5 mb-6 rounded-full text-sm font-semibold border dark:border-purple-700/50 dark:text-purple-300 dark:bg-purple-900/30"
              style={{
                background: `linear-gradient(135deg, rgba(124,58,237,0.1) 0%, rgba(16,185,129,0.1) 100%)`,
                borderColor: `${LANDING_COLORS.primary}30`,
                color: LANDING_COLORS.primary,
              }}
            >
              <HelpCircle className="h-4 w-4" />
              FREQUENTLY ASKED QUESTIONS
            </span>
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mb-4 dark:text-white"
              style={{ color: LANDING_COLORS.text.primary }}
            >
              Everything You{' '}
              <span style={{ color: LANDING_COLORS.primary }}>Need to Know</span>
            </h1>
            <p
              className="text-lg md:text-xl max-w-2xl mx-auto dark:text-slate-400"
              style={{ color: LANDING_COLORS.text.secondary }}
            >
              Answers to common questions about growing your business with Fly.
            </p>
          </RevealOnScroll>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 bg-white dark:bg-slate-900">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto space-y-16">

            {/* General Questions */}
            <RevealOnScroll>
              <div>
                <h2
                  className="text-2xl font-bold mb-6 dark:text-white"
                  style={{ color: LANDING_COLORS.text.primary }}
                >
                  General Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  {[
                    {
                      value: "web-vs-whatsapp",
                      q: "What's the difference between Fly Web Dashboard and Fly WhatsApp Agent?",
                      a: (
                        <div className="space-y-3">
                          <p><strong className="dark:text-slate-200">Fly Web Dashboard</strong> is our full-featured DIY platform where you manage everything yourself through a powerful web interface. You get complete control with detailed analytics, multi-location management, advanced posting tools, and comprehensive review management.</p>
                          <p><strong className="dark:text-slate-200">Fly WhatsApp Agent</strong> is a hands-free solution where our AI assistant handles everything for you via WhatsApp. Just message Fly to get updates, approve responses, or request reports — no dashboard, no apps, no logins required.</p>
                          <p className="font-medium dark:text-slate-300">Choose Web Dashboard if you want full control and deep analytics. Choose WhatsApp Agent if you prefer a zero-effort, conversational experience.</p>
                        </div>
                      ),
                    },
                    {
                      value: "results",
                      q: "How soon can I see results?",
                      a: "Most specialists see measurable improvements in 45-60 days as your optimized profile gains traction and attracts more client attention.",
                    },
                    {
                      value: "ownership",
                      q: "Do I still own my Google profile?",
                      a: "Yes, you always own your profile. We simply optimize and manage it on your behalf while you retain complete ownership and control.",
                    },
                    {
                      value: "industries",
                      q: "Which industries do you work with?",
                      a: "We work with local businesses across all industries including restaurants, retail stores, medical practices, salons, fitness centers, professional services, and more. Our strategies are customized for each industry's unique needs.",
                    },
                    {
                      value: "cancel",
                      q: "Can I cancel anytime?",
                      a: "Yes, you can cancel your service anytime with 30 days notice. Your Google profile remains yours, and you'll keep all the optimizations we've implemented.",
                    },
                  ].map(({ value, q, a }) => (
                    <AccordionItem key={value} value={value} className="border-0">
                      <Card className="group border border-gray-200 dark:border-slate-700 dark:bg-slate-800/60 rounded-xl hover:border-purple-300 dark:hover:border-purple-600 hover:shadow-md transition-all duration-300">
                        <AccordionTrigger className="px-6 py-5 hover:no-underline [&[data-state=open]>svg]:rotate-180">
                          <span className="text-base font-semibold text-left group-hover:text-purple-700 dark:text-slate-100 dark:group-hover:text-purple-400 transition-colors">
                            {q}
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="px-6 pb-5">
                          <div className="text-sm text-gray-600 dark:text-slate-400 leading-relaxed">
                            {typeof a === 'string' ? <p>{a}</p> : a}
                          </div>
                        </AccordionContent>
                      </Card>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </RevealOnScroll>

            {/* Fly Web Dashboard */}
            <RevealOnScroll delay={1}>
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center dark:bg-slate-700/50"
                    style={{ backgroundColor: `${LANDING_COLORS.primary}15` }}
                  >
                    <Monitor className="h-5 w-5" style={{ color: LANDING_COLORS.primary }} />
                  </div>
                  <h2
                    className="text-2xl font-bold dark:text-white"
                    style={{ color: LANDING_COLORS.text.primary }}
                  >
                    Fly Web Dashboard
                  </h2>
                </div>
                <Accordion type="single" collapsible className="space-y-3">
                  {[
                    {
                      value: "web-features",
                      q: "What features does the Web Dashboard include?",
                      a: "The Web Dashboard includes comprehensive analytics and reporting, multi-location management, AI-powered post scheduling, review management with AI response suggestions, competitor tracking, Google Business Profile optimization tools, and detailed performance insights.",
                    },
                    {
                      value: "web-technical",
                      q: "Do I need technical knowledge to use the Dashboard?",
                      a: "No technical knowledge required. The dashboard is designed to be intuitive and user-friendly. We also provide onboarding tutorials and support to help you get started quickly.",
                    },
                    {
                      value: "web-locations",
                      q: "Can I manage multiple locations?",
                      a: "Yes! The Web Dashboard supports unlimited locations. You can manage all your business locations from a single dashboard, schedule posts across locations, and view consolidated analytics.",
                    },
                    {
                      value: "web-pricing",
                      q: "What are the Web Dashboard pricing plans?",
                      a: "We offer flexible plans starting from basic profile management to enterprise solutions with advanced features. Visit our pricing page or contact us for a customized quote based on your number of locations and feature requirements.",
                    },
                  ].map(({ value, q, a }) => (
                    <AccordionItem key={value} value={value} className="border-0">
                      <Card className="group border border-gray-200 dark:border-slate-700 dark:bg-slate-800/60 rounded-xl hover:border-purple-300 dark:hover:border-purple-600 hover:shadow-md transition-all duration-300">
                        <AccordionTrigger className="px-6 py-5 hover:no-underline [&[data-state=open]>svg]:rotate-180">
                          <span className="text-base font-semibold text-left group-hover:text-purple-700 dark:text-slate-100 dark:group-hover:text-purple-400 transition-colors">
                            {q}
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="px-6 pb-5">
                          <p className="text-sm text-gray-600 dark:text-slate-400 leading-relaxed">{a}</p>
                        </AccordionContent>
                      </Card>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </RevealOnScroll>

            {/* Fly WhatsApp Agent */}
            <RevealOnScroll delay={2}>
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center dark:bg-slate-700/50"
                    style={{ backgroundColor: 'rgba(37,211,102,0.1)' }}
                  >
                    <MessageSquare className="h-5 w-5" style={{ color: '#25D366' }} />
                  </div>
                  <h2
                    className="text-2xl font-bold dark:text-white"
                    style={{ color: LANDING_COLORS.text.primary }}
                  >
                    Fly WhatsApp Agent
                  </h2>
                </div>
                <Accordion type="single" collapsible className="space-y-3">
                  {[
                    {
                      value: "whatsapp-how",
                      q: "How does the WhatsApp Agent work?",
                      a: "Simply message Fly on WhatsApp like you would a colleague. Ask for your performance report, get notified about new reviews, approve AI-generated responses, and manage your profile — all through natural conversation. No apps to install, no dashboards to learn.",
                      hoverColor: "hover:border-green-300 dark:hover:border-green-600",
                      textHover: "group-hover:text-green-700 dark:group-hover:text-green-400",
                    },
                    {
                      value: "whatsapp-features",
                      q: "What can the WhatsApp Agent do for me?",
                      a: "The WhatsApp Agent handles real-time review alerts, AI-powered review responses, 24/7 profile monitoring, monthly performance reports, keyword insights, and review generation tools. You can also request ad-hoc reports or change your preferences anytime via chat.",
                      hoverColor: "hover:border-green-300 dark:hover:border-green-600",
                      textHover: "group-hover:text-green-700 dark:group-hover:text-green-400",
                    },
                    {
                      value: "whatsapp-reviews",
                      q: "How does Fly handle my reviews?",
                      a: "When you receive a new review, Fly instantly notifies you on WhatsApp with the review details and an AI-generated response draft. You can approve it with one tap, edit it, or write your own. For negative reviews, Fly alerts you immediately so you can respond before it impacts your business.",
                      hoverColor: "hover:border-green-300 dark:hover:border-green-600",
                      textHover: "group-hover:text-green-700 dark:group-hover:text-green-400",
                    },
                    {
                      value: "whatsapp-setup",
                      q: "How long does setup take?",
                      a: "Setup takes just 5 minutes. Connect your Google Business Profile, save Fly's WhatsApp number, and you're ready to go. Our AI starts monitoring your profile and learns your brand voice immediately.",
                      hoverColor: "hover:border-green-300 dark:hover:border-green-600",
                      textHover: "group-hover:text-green-700 dark:group-hover:text-green-400",
                    },
                    {
                      value: "whatsapp-pricing",
                      q: "What does the WhatsApp Agent cost?",
                      a: "Fly Agent Lite starts at just $29/month per location for essential features. Visit our pricing page to see all plans and features, or message us on WhatsApp to get started with a free trial.",
                      hoverColor: "hover:border-green-300 dark:hover:border-green-600",
                      textHover: "group-hover:text-green-700 dark:group-hover:text-green-400",
                    },
                    {
                      value: "whatsapp-auto",
                      q: "Can Fly respond to reviews automatically?",
                      a: "Yes! You can enable auto-response for positive reviews (4-5 stars) while keeping manual approval for negative reviews. This way, happy customers get immediate engagement while you maintain control over sensitive situations.",
                      hoverColor: "hover:border-green-300 dark:hover:border-green-600",
                      textHover: "group-hover:text-green-700 dark:group-hover:text-green-400",
                    },
                  ].map(({ value, q, a, hoverColor, textHover }) => (
                    <AccordionItem key={value} value={value} className="border-0">
                      <Card className={`group border border-gray-200 dark:border-slate-700 dark:bg-slate-800/60 rounded-xl ${hoverColor} hover:shadow-md transition-all duration-300`}>
                        <AccordionTrigger className="px-6 py-5 hover:no-underline [&[data-state=open]>svg]:rotate-180">
                          <span className={`text-base font-semibold text-left ${textHover} dark:text-slate-100 transition-colors`}>
                            {q}
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="px-6 pb-5">
                          <p className="text-sm text-gray-600 dark:text-slate-400 leading-relaxed">{a}</p>
                        </AccordionContent>
                      </Card>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </RevealOnScroll>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQ;
